import { useCallback, useMemo, useRef, useState } from "react";
import {
  getCountFromServer,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  startAfter,
  where,
} from "../firebase";
import { massTextMessageConverter, uniqueVals } from "@markit/common.utils";
import {
  DMAutomation,
  DMAutomationContent,
  DMAutomationContentType,
  DMAutomationTrigger,
} from "@markit/common.types";
import {
  getCampaignMassTextData,
  getUserCampaignData,
  getUserDmAutomationContentsRef,
  getUserDmAutomationSubmissionsRef,
  getUserDmAutomationTriggersRef,
  getUserDmAutomationsRef,
} from "../utils/FirebaseUtils";
import { getMassTextAttachments } from "../utils/textingUtils";
import useOnUnmount from "./useOnUnmount";

type useLoadDMAutomationItemsProps = {
  userId: string;
  windowSize: number;
  onlyCampaignDMAutomations?: boolean;
  selectedAccountIds?: string[]; // all accounts if undefined
  trustedSender: boolean; // for converting a send-as-text automation's campaign message
};

export const useLoadDMAutomationItems = (
  props: useLoadDMAutomationItemsProps
) => {
  const {
    userId,
    windowSize,
    onlyCampaignDMAutomations,
    selectedAccountIds,
    trustedSender,
  } = props;
  const [fetchedDMAutomations, setFetchedDMAutomations] = useState<
    DMAutomation[]
  >([]);
  const [fetchedTriggers, setFetchedTriggers] = useState<DMAutomationTrigger[]>(
    []
  );
  const [fetchedContents, setFetchedContents] = useState<DMAutomationContent[]>(
    []
  );
  // dmAutomationId -> message, for send-as-text automations (their message lives on
  // the linked campaign's initial MassText, not a DMAutomationContent doc).
  const [campaignMessages, setCampaignMessages] = useState<
    Record<string, string>
  >({});
  const [submissionCounts, setSubmissionCounts] = useState<
    Record<string, number>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadedAllDMAutomations, setLoadedAllDMAutomations] = useState(false);
  const [
    lastDMAutomationVisibleTimestamp,
    setLastDMAutomationVisibleTimestamp,
  ] = useState("");
  const unsubscribeArrayRef = useRef<(() => void)[]>([]);
  const lastDMAutomationDocRef = useRef<
    QueryDocumentSnapshot<DMAutomation> | undefined
  >(undefined);

  useOnUnmount(() => {
    unsubscribeArrayRef.current.forEach(
      (unsubscribe) => unsubscribe && unsubscribe()
    );
    unsubscribeArrayRef.current = [];
  }, []);

  const addDMAutomationData = useCallback((items: DMAutomation[]) => {
    setFetchedDMAutomations((prev) =>
      uniqueVals(items.concat(prev), (dm) => dm.id).sort(
        (a, b) =>
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
      )
    );
  }, []);

  const removeDMAutomationData = useCallback((dm: DMAutomation) => {
    setFetchedDMAutomations((prev) => {
      const index = prev.findIndex((fetched) => fetched.id === dm.id);
      if (index !== -1) {
        prev.splice(index, 1);
      }
      return [...prev];
    });
  }, []);

  const addDMAutomationTriggersData = useCallback(
    (dmAutomationId: string, items: DMAutomationTrigger[]) => {
      setFetchedTriggers((prev) => {
        const withoutExisting = prev.filter(
          (trigger) =>
            (trigger as DMAutomationTrigger & { dmAutomationId?: string })
              .dmAutomationId !== dmAutomationId
        );
        return uniqueVals(items.concat(withoutExisting), (item) => item.id);
      });
    },
    []
  );

  const removeDMAutomationTriggersData = useCallback(
    (dmAutomationId: string) => {
      setFetchedTriggers((prev) => {
        return prev.filter(
          (trigger) =>
            (trigger as DMAutomationTrigger & { dmAutomationId?: string })
              .dmAutomationId !== dmAutomationId
        );
      });
    },
    []
  );

  const addDMAutomationContentData = useCallback(
    (dmAutomationId: string, items: DMAutomationContent[]) => {
      setFetchedContents((prev) => {
        const withoutExisting = prev.filter(
          (content) => content.dmAutomationId !== dmAutomationId
        );
        return uniqueVals(items.concat(withoutExisting), (item) => item.id);
      });
    },
    []
  );

  const removeDMAutomationContentData = useCallback(
    (dmAutomationId: string) => {
      setFetchedContents((prev) =>
        prev.filter((content) => content.dmAutomationId !== dmAutomationId)
      );
    },
    []
  );

  const isFinished = useMemo(
    () => loadedAllDMAutomations,
    [loadedAllDMAutomations]
  );

  const updateData = useCallback(
    async (foundData: DMAutomation[]) => {
      addDMAutomationData(foundData);
      await Promise.all(
        foundData.map(async (dm) => {
          const triggersRef = getUserDmAutomationTriggersRef(userId, dm.id);
          const submissionsRef = getUserDmAutomationSubmissionsRef(
            userId,
            dm.id
          );
          const [triggerSnap, contentSnap, campaignMessage, submissionCountSnap] =
            await Promise.all([
              getDocs(query(triggersRef, orderBy("createdAt", "desc"))),
              // A send-as-text automation has no DM_MESSAGE content - its message
              // lives on a MassText instead (fetched separately below).
              dm.campaignId
                ? Promise.resolve(undefined)
                : getDocs(
                    query(
                      getUserDmAutomationContentsRef(userId, dm.id),
                      where("type", "==", DMAutomationContentType.DM_MESSAGE),
                      limit(1)
                    )
                  ),
              // A send-as-text automation's message is the initial MassText on its
              // linked campaign - the campaign doc names which MassText, then the
              // MassText itself has the message. Run through massTextMessageConverter
              // so attachment placeholder ids resolve to their real links instead of
              // showing up as a raw attachmentId in the message text.
              dm.campaignId
                ? (async () => {
                    const campaign = await getUserCampaignData(
                      userId,
                      dm.campaignId
                    );
                    if (!campaign?.initialTextId) {
                      return undefined;
                    }
                    const massText = await getCampaignMassTextData(
                      userId,
                      dm.campaignId,
                      campaign.initialTextId
                    );
                    if (!massText) {
                      return undefined;
                    }
                    const attachments = await getMassTextAttachments(
                      userId,
                      massText
                    );
                    return massTextMessageConverter(
                      massText.message,
                      attachments,
                      trustedSender
                    );
                  })()
                : Promise.resolve(undefined),
              getCountFromServer(submissionsRef),
            ]);
          addDMAutomationTriggersData(
            dm.id,
            triggerSnap.docs.map((doc) => doc.data())
          );
          if (contentSnap) {
            addDMAutomationContentData(
              dm.id,
              contentSnap.docs.map((doc) => doc.data())
            );
          }
          if (campaignMessage) {
            setCampaignMessages((prev) => ({
              ...prev,
              [dm.id]: campaignMessage,
            }));
          }
          setSubmissionCounts((prev) => ({
            ...prev,
            [dm.id]: submissionCountSnap.data().count,
          }));
        })
      );
    },
    [
      addDMAutomationData,
      addDMAutomationTriggersData,
      addDMAutomationContentData,
      userId,
      trustedSender,
    ]
  );

  const existingSnapshot = useMemo(
    () => fetchedDMAutomations,
    [fetchedDMAutomations]
  );

  const initialUpdateData = useCallback(
    async (foundData: DMAutomation[], skipDedup = false) => {
      // skipDedup skips deduplication after a reset, avoiding incorrect filtering from stale existingSnapshot.
      const newItems = skipDedup
        ? foundData
        : foundData.filter(
            (loaded) =>
              !existingSnapshot.some(
                (existing) =>
                  existing.id === loaded.id &&
                  new Date(existing.updatedAt).getTime() ===
                    new Date(loaded.updatedAt).getTime()
              )
          );

      if (foundData.length > 0 && newItems.length > 0) {
        await updateData(newItems);
      }
      setIsLoading(false);

      if (foundData.length) {
        setLastDMAutomationVisibleTimestamp(
          foundData[foundData.length - 1].updatedAt
        );
      }
      if (foundData.length < windowSize) {
        setLoadedAllDMAutomations(true);
      }
    },
    [existingSnapshot, updateData, windowSize]
  );

  const liveUpdateDMAutomations = useCallback(
    async (
      type: "added" | "modified" | "removed",
      dm: DMAutomation,
      baseline: DMAutomation[]
    ) => {
      if (
        (type === "added" && !baseline.some((row) => row.id === dm.id)) ||
        type === "modified"
      ) {
        await updateData([dm]);
      } else if (type === "removed") {
        removeDMAutomationData(dm);
        removeDMAutomationTriggersData(dm.id);
        removeDMAutomationContentData(dm.id);
      }
    },
    [
      removeDMAutomationData,
      removeDMAutomationTriggersData,
      removeDMAutomationContentData,
      updateData,
    ]
  );

  const buildDMAutomationsQuery = useCallback(
    (startAfterDoc?: QueryDocumentSnapshot<DMAutomation>) => {
      const ref = getUserDmAutomationsRef(userId);
      return query(
        ref,
        ...(onlyCampaignDMAutomations
          ? [where("campaignId", "!=", ""), orderBy("campaignId")]
          : []),
        ...(selectedAccountIds && selectedAccountIds.length > 0
          ? [where("igAccountIds", "array-contains-any", selectedAccountIds)]
          : []),
        orderBy("updatedAt", "desc"),
        ...(startAfterDoc ? [startAfter(startAfterDoc)] : []),
        limit(windowSize)
      );
    },
    [userId, windowSize, onlyCampaignDMAutomations, selectedAccountIds]
  );

  const loadDMAutomations = async (skipDedup = false) => {
    setIsLoading(true);
    const dmQuery = buildDMAutomationsQuery();

    const querySnap = await getDocs(dmQuery);
    lastDMAutomationDocRef.current = querySnap.docs[querySnap.docs.length - 1];
    const found = querySnap.docs.map((doc) => doc.data());
    await initialUpdateData(found, skipDedup);

    const unsubscribe = onSnapshot(dmQuery, (snapshot) => {
      snapshot.docChanges().forEach(async (message) => {
        const dm = message.doc.data();
        await liveUpdateDMAutomations(message.type, dm, found);
      });
    });
    unsubscribeArrayRef.current.push(unsubscribe);
  };

  // Changing which accounts are selected changes the query itself - drop the
  // current page/cursor/listeners and start over against the new query.
  const resetAndReloadDMAutomations = async () => {
    unsubscribeArrayRef.current.forEach(
      (unsubscribe) => unsubscribe && unsubscribe()
    );
    unsubscribeArrayRef.current = [];
    lastDMAutomationDocRef.current = undefined;
    setFetchedDMAutomations([]);
    setFetchedTriggers([]);
    setFetchedContents([]);
    setCampaignMessages({});
    setSubmissionCounts({});
    setLoadedAllDMAutomations(false);
    setLastDMAutomationVisibleTimestamp("");
    await loadDMAutomations(true);
  };

  const loadMoreDMAutomations = async () => {
    if (!isFinished && !isLoading && lastDMAutomationDocRef.current) {
      setIsLoading(true);
      const dmQuery = buildDMAutomationsQuery(lastDMAutomationDocRef.current);

      const querySnap = await getDocs(dmQuery);
      if (querySnap.docs.length) {
        lastDMAutomationDocRef.current =
          querySnap.docs[querySnap.docs.length - 1];
      }
      const found = querySnap.docs.map((doc) => doc.data());
      await initialUpdateData(found);

      const unsubscribe = onSnapshot(dmQuery, (snapshot) => {
        snapshot.docChanges().forEach(async (message) => {
          const dm = message.doc.data();
          await liveUpdateDMAutomations(message.type, dm, found);
        });
      });
      unsubscribeArrayRef.current.push(unsubscribe);
    }
  };

  return {
    fetchedDMAutomations,
    fetchedTriggers,
    fetchedContents,
    campaignMessages,
    submissionCounts,
    isLoading,
    isFinished,
    lastDMAutomationVisibleTimestamp,
    loadDMAutomations,
    loadMoreDMAutomations,
    resetAndReloadDMAutomations,
  };
};
